import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { login, createUser, createBusiness, linkUserToBusiness } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import Toast from '../components/Toast';
import {
  FileText,
  Users,
  Package,
  TrendingUp,
  BarChart3,
  Shield,
  Loader2
} from 'lucide-react';
import './Login.css';

const Login = () => {
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLogin, setIsLogin] = useState(true);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
    });
    const [error, setError] = useState('');
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [showLogoutToast, setShowLogoutToast] = useState(false);
    const [logoutToastMessage, setLogoutToastMessage] = useState('');
    const [logoutToastType, setLogoutToastType] = useState('success');
    const [isBusinessMode, setIsBusinessMode] = useState(false);
    const [createdUserId, setCreatedUserId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [businessForm, setBusinessForm] = useState({
        businessName: '',
        contactNumber: '',
        address: '',
        profileUrl: '',
        email: '' // optional
    });
    
    // Use refs to prevent duplicate API calls
    const submittingRef = useRef(false);
    const businessSubmittingRef = useRef(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleBusinessChange = (e) => {
        setBusinessForm({
            ...businessForm,
            [e.target.name]: e.target.value
        });
    };

    // Auto-hide success message after 5 seconds
    useEffect(() => {
        if (showSuccessMessage) {
            const fadeTimer = setTimeout(() => {
                setIsFadingOut(true);
            }, 4500); // Start fade out 0.5 seconds before hiding

            const hideTimer = setTimeout(() => {
                setShowSuccessMessage(false);
                setError('');
                setIsFadingOut(false);
            }, 5000);

            return () => {
                clearTimeout(fadeTimer);
                clearTimeout(hideTimer);
            };
        }
    }, [showSuccessMessage]);

    // Check for logout / session-expired messages
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const logoutSuccess = urlParams.get('logout');
        const sessionExpired = urlParams.get('session');
        
        if (sessionExpired === 'expired') {
            setLogoutToastMessage(t('login.sessionExpired') || 'Your session has expired. Please login again.');
            setLogoutToastType('error');
            setShowLogoutToast(true);
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (logoutSuccess === 'success') {
            setLogoutToastMessage(t('login.logoutSuccess'));
            setLogoutToastType('success');
            setShowLogoutToast(true);
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [location.search, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Prevent duplicate submissions
        if (submittingRef.current || isLoading) return;
        
        submittingRef.current = true;
        setIsLoading(true);
        setError('');

        try {
            if (isLogin) {
                // Handle Login
                const result = await login(formData.email, formData.password);
                if (result.success) {
                    // Small delay to ensure localStorage is updated before navigation
                    // This ensures the Layout component reads the correct business data
                    await new Promise(resolve => setTimeout(resolve, 100));
                    navigate('/dashboard');
                } else {
                    setError(result.message || 'Login failed');
                    setIsLoading(false);
                }
            } else {
                // Handle User Creation
                const result = await createUser(formData);
                if (result.success) {
                    const newUserId = result?.data?.id;
                    setCreatedUserId(newUserId || '');
                    // Pre-fill email from registration as optional
                    setBusinessForm(prev => ({ ...prev, email: formData.email }));
                    // Switch to full-page business creation view
                    setIsBusinessMode(true);
                    setIsLoading(false);
                } else {
                    setError(result.message || 'Account creation failed');
                    setIsLoading(false);
                }
            }
        } catch (error) {
            setError('An error occurred. Please try again.');
            setIsLoading(false);
        } finally {
            submittingRef.current = false;
        }
    };

    const handleBusinessSubmit = async (e) => {
        e.preventDefault();
        
        // Prevent duplicate submissions
        if (businessSubmittingRef.current || isLoading) return;
        
        if (!createdUserId) {
            setError('Missing user id for business creation.');
            return;
        }
        
        businessSubmittingRef.current = true;
        setIsLoading(true);
        setError('');
        try {
            // Business APIs require JWT — auto-login with the account just created
            const loginResult = await login(formData.email, formData.password);
            if (!loginResult.success) {
                setError(loginResult.message || 'Please login first, then create your business.');
                setIsLoading(false);
                return;
            }

            const result = await createBusiness({
                userId: createdUserId,
                business: businessForm
            });
            if (result.success) {
                const createdBusiness = result.data;
                const businessId = createdBusiness?.id;
                if (businessId) {
                    await linkUserToBusiness({
                        businessId,
                        userId: createdUserId
                    });
                    localStorage.setItem('selectedBusiness', JSON.stringify(createdBusiness));
                    localStorage.setItem('businesses', JSON.stringify([createdBusiness]));
                    window.dispatchEvent(new Event('localStorageUpdated'));
                }
                navigate('/dashboard');
            } else {
                setError(result.message || 'Business creation failed');
                setIsLoading(false);
            }
        } catch (error) {
            setError('An error occurred. Please try again.');
            setIsLoading(false);
        } finally {
            businessSubmittingRef.current = false;
        }
    };

    const features = [
        { icon: FileText, title: t('login.features.loanManagement'), desc: t('login.featureDescriptions.loanManagement') },
        { icon: Users, title: t('login.features.borrowerTracking'), desc: t('login.featureDescriptions.borrowerTracking') },
        { icon: Package, title: t('login.features.collateralManagement'), desc: t('login.featureDescriptions.collateralManagement') },
        { icon: TrendingUp, title: t('login.features.profitabilityAnalysis'), desc: t('login.featureDescriptions.profitabilityAnalysis') },
        { icon: BarChart3, title: t('login.features.advancedAnalytics'), desc: t('login.featureDescriptions.advancedAnalytics') },
        { icon: Shield, title: t('login.features.secureReliable'), desc: t('login.featureDescriptions.secureReliable') }
    ];

    return (
        <div className="login-container">
            {showLogoutToast && (
                <Toast 
                    message={logoutToastMessage || t('login.logoutSuccess')} 
                    type={logoutToastType} 
                    duration={5000}
                    onClose={() => setShowLogoutToast(false)}
                />
            )}
            
            <div className="login-content-wrapper">
            {/* Left Side - Logo and Welcome */}
            <div className="login-left">
                <div className="login-logo-section">
                    <div className="logo-container">
                        <img 
                            src="/LMS_SIDE_LOGO.png" 
                            alt="Loan Management Logo" 
                            className="login-logo"
                        />
                    </div>
                    <h1 className="welcome-title">{t('login.modernLMS')}</h1>
                    <p className="welcome-subtitle">{t('login.comprehensiveSolution')}</p>
                </div>

                {/* Features Section */}
                <div className="features-section">
                    <h3 className="features-title">What We Offer</h3>
                    <div className="features-grid">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <div key={index} className="feature-item">
                                    <div className="feature-icon">
                                        <Icon size={24} />
                                    </div>
                                    <div className="feature-content">
                                        <h4 className="feature-name">{feature.title}</h4>
                                        <p className="feature-desc">{feature.desc}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="login-right">
                <div className="login-box">
                <h2>
                    {isBusinessMode
                        ? t('login.businessSetup')
                        : isLogin ? t('login.signIn') : t('login.createAccount')}
                </h2>
                {error && (
                    <div className={`message ${showSuccessMessage ? 'success-message' : 'error-message'} ${isFadingOut ? 'fade-out' : ''}`}>
                        {error}
                    </div>
                )}
                {!isBusinessMode && (
                    <form onSubmit={handleSubmit}>
                    {!isLogin && (
                        <>
                            <div className="form-group">
                                <input
                                    type="text"
                                    name="firstName"
                                    placeholder={t('login.firstName')}
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required={!isLogin}
                                />
                            </div>
                            <div className="form-group">
                                <input
                                    type="text"
                                    name="lastName"
                                    placeholder={t('login.lastName')}
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required={!isLogin}
                                />
                            </div>
                        </>
                    )}
                    <div className="form-group">
                        <input
                            type="email"
                            name="email"
                            placeholder={t('login.email')}
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <input
                            type="password"
                            name="password"
                            placeholder={t('login.password')}
                            value={formData.password}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <button 
                        type="submit" 
                        className="submit-button"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="spinner-icon" size={18} />
                                {isLogin ? t('common.loading') : t('common.loading')}
                            </>
                        ) : (
                            isLogin ? t('login.loginBtn') : t('login.signUpBtn')
                        )}
                    </button>
                    </form>
                )}

                {isBusinessMode && (
                    <form onSubmit={handleBusinessSubmit}>
                        <div className="form-group">
                            <input
                                type="text"
                                name="businessName"
                                placeholder={t('login.businessName')}
                                value={businessForm.businessName}
                                onChange={handleBusinessChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <input
                                type="tel"
                                name="contactNumber"
                                placeholder={t('login.contactNumber')}
                                value={businessForm.contactNumber}
                                onChange={handleBusinessChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <input
                                type="text"
                                name="address"
                                placeholder={t('login.address')}
                                value={businessForm.address}
                                onChange={handleBusinessChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <input
                                type="email"
                                name="email"
                                placeholder={t('login.businessEmail')}
                                value={businessForm.email}
                                onChange={handleBusinessChange}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                            <button 
                                type="button" 
                                className="toggle-button" 
                                onClick={() => setIsBusinessMode(false)}
                                disabled={isLoading}
                            >
                                {t('login.backToSignUp')}
                            </button>
                            <button 
                                type="submit" 
                                className="submit-button"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="spinner-icon" size={18} />
                                        {t('common.loading')}
                                    </>
                                ) : (
                                    t('login.continue')
                                )}
                            </button>
                        </div>
                    </form>
                )}

                {!isBusinessMode && (
                    <p className="toggle-form">
                        {isLogin ? t('login.dontHaveAccount') : t('login.alreadyHaveAccount')}{' '}
                        <button 
                            className="toggle-button"
                            onClick={() => setIsLogin(!isLogin)}
                        >
                            {isLogin ? t('login.signUpHere') : t('login.signInHere')}
                        </button>
                    </p>
                )}
                </div>
            </div>
            </div>

            {/* Footer */}
            <footer 
                className="login-footer"
                style={{
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    padding: '1.25rem 2rem',
                    boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.3)',
                    width: '100%',
                    flexShrink: 0,
                    zIndex: 10,
                    position: 'relative'
                }}
            >
                <div 
                    className="footer-content"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        flexWrap: 'wrap',
                        gap: '0.5rem'
                    }}
                >
                    <span 
                        className="footer-text"
                        style={{
                            fontSize: '0.875rem',
                            color: 'rgba(255, 255, 255, 0.95)',
                            textAlign: 'center',
                            fontWeight: 400,
                            lineHeight: '1.5'
                        }}
                    >
                        © 2025 Loan Management. {t('common.allRightsReserved')} |{' '}
                        {language === 'hi' ? (
                            <>
                                <a 
                                    href="https://www.seervitechlabs.com/" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="footer-link"
                                    style={{
                                        color: '#ffffff',
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                        marginLeft: '0.25rem',
                                        transition: 'all 0.2s ease',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.color = '#fef3c7';
                                        e.target.style.textDecoration = 'underline';
                                        e.target.style.textShadow = '0 2px 4px rgba(0, 0, 0, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.color = '#ffffff';
                                        e.target.style.textDecoration = 'none';
                                        e.target.style.textShadow = '0 1px 2px rgba(0, 0, 0, 0.2)';
                                    }}
                                >
                                    Seervi Tech Labs
                                </a>
                                {' द्वारा निर्मित'}
                            </>
                        ) : (
                            <>
                                {t('common.developedBy')}{' '}
                                <a 
                                    href="https://www.seervitechlabs.com/" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="footer-link"
                                    style={{
                                        color: '#ffffff',
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                        transition: 'all 0.2s ease',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.color = '#fef3c7';
                                        e.target.style.textDecoration = 'underline';
                                        e.target.style.textShadow = '0 2px 4px rgba(0, 0, 0, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.color = '#ffffff';
                                        e.target.style.textDecoration = 'none';
                                        e.target.style.textShadow = '0 1px 2px rgba(0, 0, 0, 0.2)';
                                    }}
                                >
                                    Seervi Tech Labs
                                </a>
                            </>
                        )}
                    </span>
                </div>
            </footer>
        </div>
    );
};

export default Login;